// VpnAvailabilityHistory.jsx — tren availability jangka panjang VPN tunnel.
// Sumber: tabel permanen vpn_tunnel_daily_summary via
//   GET /api/periodic-report/vpn-daily/history?from&to&group=day|week|month
// Availability agregat = rata-rata tertimbang jumlah sampel (sama dengan backend).
import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';
import { Btn, useApi } from './tmShared';

const B = '/api/periodic-report/vpn-daily';
const KINDS = [
  { key: 'all', label: 'All tunnels', color: '#0f172a' },
  { key: 'aws', label: 'AWS S2S VPN', color: '#2563eb' },
  { key: 'bovpn', label: 'WatchGuard BOVPN', color: '#d97706' },
  { key: 'gre', label: 'GRE PGOC', color: '#7c3aed' },
];
const RANGES = [
  { key: '90', label: '90 days', days: 90 },
  { key: '365', label: '1 year', days: 365 },
  { key: '730', label: '2 years', days: 730 },
  { key: 'all', label: 'All', days: null },
];
const TARGET = 99.5;

const ymd = (d) => d.toISOString().slice(0, 10);
const pct = (v) => (v == null ? '—' : `${v.toFixed(2)}%`);
const pctColor = (v) => (v == null ? '#94a3b8' : v >= 99.9 ? '#16a34a' : v >= TARGET ? '#ca8a04' : '#dc2626');
const fmtMin = (m) => {
  const v = Math.round(m || 0);
  if (v < 60) return `${v} min`;
  return `${Math.floor(v / 60)}h ${String(v % 60).padStart(2, '0')}m`;
};

// Gabungkan baris per tunnel menjadi satu angka tertimbang sampel.
function weigh(rows) {
  let s = 0, up = 0, down = 0, ev = 0;
  for (const r of rows) {
    if (r.availability != null && r.samples) { s += r.samples; up += r.availability * r.samples; }
    down += r.down_minutes || 0; ev += r.events || 0;
  }
  return { availability: s ? up / s : null, down, events: ev };
}

export default function VpnAvailabilityHistory() {
  const api = useApi();
  const [group, setGroup] = useState('month');
  const [range, setRange] = useState('365');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const r = RANGES.find((x) => x.key === range);
      const qs = new URLSearchParams({ group });
      if (r.days) qs.set('from', ymd(new Date(Date.now() - r.days * 864e5)));
      const d = await api(`${B}/history?${qs.toString()}`);
      setRows(d.rows || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [api, group, range]);
  useEffect(() => { load(); }, [load]);

  const backfill = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const d = await api(`${B}/history/backfill`, { method: 'POST', body: JSON.stringify({ days: 120 }) });
      const saved = (d.results || []).filter((x) => x.tunnels).length;
      setMsg(`Recalculated ${saved} day(s) from raw data still available.`);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const series = useMemo(() => {
    const byBucket = {};
    rows.forEach((r) => { (byBucket[r.bucket] = byBucket[r.bucket] || []).push(r); });
    return Object.keys(byBucket).sort().map((b) => {
      const list = byBucket[b];
      const o = { bucket: b };
      KINDS.forEach((k) => {
        const sel = k.key === 'all' ? list : list.filter((r) => r.kind === k.key);
        const w = weigh(sel);
        o[k.key] = w.availability == null ? null : Number(w.availability.toFixed(3));
      });
      return o;
    });
  }, [rows]);

  const tunnels = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      const k = `${r.kind}|${r.grp}|${r.tunnel}`;
      if (!m[k]) m[k] = { kind: r.kind, grp: r.grp, tunnel: r.tunnel, rows: [], days: 0, worst: null };
      m[k].rows.push(r); m[k].days += r.days || 0;
      if (r.availability != null && (m[k].worst == null || r.availability < m[k].worst.availability)) m[k].worst = r;
    });
    return Object.values(m).map((t) => ({ ...t, ...weigh(t.rows) }))
      .sort((a, b) => (a.availability ?? 101) - (b.availability ?? 101));
  }, [rows]);

  const overall = useMemo(() => weigh(rows), [rows]);
  const yMin = useMemo(() => {
    const vals = series.flatMap((s) => KINDS.map((k) => s[k.key])).filter((v) => v != null);
    const lo = vals.length ? Math.min(...vals) : 99;
    return Math.max(0, Math.floor(Math.min(lo, TARGET) - 0.5));
  }, [series]);

  const card = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 };
  const sel = { padding: '6px 8px', fontSize: 12.5, border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff' };
  const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #e2e8f0', textTransform: 'uppercase' };
  const td = { padding: '6px 8px', fontSize: 12.5, borderBottom: '1px solid #f1f5f9', color: '#334155' };
  const groupLabel = { day: 'day', week: 'week', month: 'month' }[group];

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', marginRight: 'auto' }}>
          📈 Long-term availability
          <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
            {pct(overall.availability)} overall · {overall.events} event(s) · {fmtMin(overall.down)} down
          </span>
        </div>
        <select style={sel} value={group} onChange={(e) => setGroup(e.target.value)}>
          <option value="day">Per day</option>
          <option value="week">Per week</option>
          <option value="month">Per month</option>
        </select>
        <select style={sel} value={range} onChange={(e) => setRange(e.target.value)}>
          {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
        </select>
        <Btn size="sm" disabled={busy} onClick={load}>{busy ? '⏳' : '🔄'} Refresh</Btn>
        <Btn size="sm" disabled={busy} onClick={backfill}>♻️ Recalculate</Btn>
      </div>
      {err && <div style={{ color: '#dc2626', fontSize: 12.5, marginBottom: 8 }}>{err}</div>}
      {msg && <div style={{ color: '#16a34a', fontSize: 12.5, marginBottom: 8 }}>{msg}</div>}

      {!series.length ? (
        <div style={{ fontSize: 12.5, color: '#64748b', padding: '20px 0' }}>
          No history yet. The summary is saved automatically after each daily window closes.
        </div>
      ) : (
        <>
          <div style={{ width: '100%', height: 280, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 4px 0 0' }}>
            <ResponsiveContainer>
              <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} minTickGap={16} />
                <YAxis domain={[yMin, 100]} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false}
                  tickFormatter={(v) => `${v}%`} width={52} allowDecimals />
                <Tooltip formatter={(v, n) => [pct(v), KINDS.find((k) => k.key === n)?.label || n]}
                  labelFormatter={(l) => `${groupLabel} ${l}`} contentStyle={{ fontSize: 12 }} />
                <Legend formatter={(v) => KINDS.find((k) => k.key === v)?.label || v} wrapperStyle={{ fontSize: 12 }} />
                <ReferenceLine y={TARGET} stroke="#dc2626" strokeDasharray="4 4"
                  label={{ value: `target ${TARGET}%`, position: 'insideBottomRight', fontSize: 10, fill: '#dc2626' }} />
                {KINDS.map((k) => (
                  <Line key={k.key} type="monotone" dataKey={k.key} stroke={k.color}
                    strokeWidth={k.key === 'all' ? 2.5 : 1.5} dot={series.length <= 31 ? { r: 2.5 } : false}
                    connectNulls isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', margin: '14px 0 6px' }}>
            Per tunnel (selected range, lowest availability first)
          </div>
          <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Type</th><th style={th}>Group</th><th style={th}>Tunnel</th>
                <th style={{ ...th, textAlign: 'right' }}>Days</th><th style={{ ...th, textAlign: 'right' }}>Availability</th>
                <th style={{ ...th, textAlign: 'right' }}>Events</th><th style={{ ...th, textAlign: 'right' }}>Down</th>
                <th style={th}>Worst {groupLabel}</th>
              </tr></thead>
              <tbody>
                {tunnels.map((t) => (
                  <tr key={`${t.kind}|${t.grp}|${t.tunnel}`}>
                    <td style={td}>{KINDS.find((k) => k.key === t.kind)?.label || t.kind}</td>
                    <td style={td}>{t.grp}</td>
                    <td style={td}>{t.tunnel}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{t.days}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: pctColor(t.availability) }}>{pct(t.availability)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{t.events}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{fmtMin(t.down)}</td>
                    <td style={td}>{t.worst ? `${t.worst.bucket} (${pct(t.worst.availability)})` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
